/**
 * Safe rendering utilities for AI-generated metadata
 * Ensures no objects/arrays are passed directly to React children
 */

/**
 * Convert any value to a safe string representation for React rendering
 * @param {*} value - Value to render
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Safe string representation
 */
export const renderSafe = (value, maxLength = 500) => {
  if (value === null || value === undefined) {
    return "—";
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    const result = String(value);
    return result.length > maxLength ? result.substring(0, maxLength) + "..." : result;
  }

  if (Array.isArray(value)) {
    try {
      const jsonStr = JSON.stringify(value);
      return jsonStr.length > maxLength ? jsonStr.substring(0, maxLength) + "..." : jsonStr;
    } catch {
      return "[Array]";
    }
  }

  if (typeof value === "object") {
    try {
      const jsonStr = JSON.stringify(value);
      return jsonStr.length > maxLength ? jsonStr.substring(0, maxLength) + "..." : jsonStr;
    } catch {
      return "[Object]";
    }
  }

  return String(value).substring(0, maxLength);
};

/**
 * Safely extract tags from various formats
 * @param {*} tags - Tags in any format
 * @returns {string[]} - Array of tag strings
 */
export const normalizeTags = (tags) => {
  if (!tags) return [];
  
  if (Array.isArray(tags)) {
    return tags.map(t => renderSafe(t, 50)).filter(Boolean);
  }
  
  if (typeof tags === "string") {
    return tags.split(",").map(t => t.trim()).filter(Boolean);
  }
  
  return [];
};

/**
 * Safely normalize AI suggestions response
 * @param {Object} suggestions - Raw suggestions object
 * @returns {Object} - Normalized suggestions safe for rendering
 */
export const normalizeSuggestions = (suggestions) => {
  if (!suggestions) {
    return {
      title: null,
      doc_type: null,
      tags: [],
      fields: {}
    };
  }

  const normalized = {
    title: suggestions.title ? renderSafe(suggestions.title, 200) : null,
    doc_type: suggestions.doc_type ? renderSafe(suggestions.doc_type, 100) : null,
    tags: normalizeTags(suggestions.tags),
    fields: {}
  };

  // Normalize fields - ensure all values are strings
  const rawFields = suggestions.fields || suggestions.suggested_fields || {};
  if (typeof rawFields === "object" && !Array.isArray(rawFields)) {
    Object.entries(rawFields).forEach(([key, value]) => {
      normalized.fields[String(key)] = renderSafe(value, 300);
    });
  }

  return normalized;
};

export default { renderSafe, normalizeTags, normalizeSuggestions };
